import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { CookiePolicy } from '@ott/cookie-policy';
import { Container } from '@ott/grid';
import { authActions } from '@ott/solar-auth';
import Footer from '@ott/solar-footer';
import Header, { HEADER_THEME_DARK } from '@ott/solar-header';
import sendKM from '@ott/utility-km';

import { getAuthSelector } from 'src/redux/selectors/commonSelectors';
import { RootState } from 'src/redux/store';

import SupportChat from 'src/components/blocks/SupportChat';

import { COOKIE_POLICY_LINK } from 'src/constants/common';

import useIsWebview from 'src/hooks/useIsWebview';
import useTypedSelector from 'src/hooks/useTypedSelector';

import About from './blocks/About';
import Cards from './blocks/Cards';
import MainScreen from './blocks/MainScreen';
import MobileBanner from './blocks/MobileBanner';
import SocialLinks from './blocks/SocialLinks';

import styles from './AboutUsPage.scss';

const AboutUsPage = () => {
  const isWebview = useIsWebview();
  const history = useHistory();
  const auth = useTypedSelector(getAuthSelector);
  const ssr: boolean = useTypedSelector((state: RootState) => state.common.ssr);
  const dispatch = useDispatch();
  const isAuthorized = auth?.data?.auth;

  const [newMessages, setNewMessages] = useState(0);
  const [isSupportChatVisible, setIsSupportChatVisible] = useState(false);
  const [isSupportChatOpen, setIsSupportChatOpen] = useState(false);

  const handleNewMessages = useCallback((count: number) => {
    setNewMessages(count);
  }, []);

  const handleOpenSupportChat = useCallback((isOpen: boolean) => {
    setIsSupportChatOpen(isOpen);
  }, []);

  const onSupportChatVisible = useCallback((isVisible: boolean) => {
    setIsSupportChatVisible(isVisible);
  }, []);

  useEffect(() => {
    sendKM('SOLAR_ABOUT_PAGE_SHOW');

    if (auth.infoStatus.initial) {
      dispatch(authActions.getBasicUserInfo());
    }
  }, []);

  if (ssr) {
    return null;
  }

  return (
    <>
      {!isWebview && (
        <>
          <CookiePolicy cookieLink={COOKIE_POLICY_LINK} />
          <Header
            theme={HEADER_THEME_DARK}
            history={history}
            showNavigation={true}
            isTransparent={true}
            supportNewMessagesCount={newMessages}
            onOpenSupportChat={handleOpenSupportChat}
            isSupportChatVisible={isSupportChatVisible}
          />
        </>
      )}
      <Container className={styles.container}>
        <MainScreen />
        <About />
        <Cards />
        <MobileBanner />
        <SocialLinks />
      </Container>
      {!isWebview && <Footer />}
      <SupportChat
        isUserAuthorized={isAuthorized}
        isSolar={true}
        isChatButtonHidden={true}
        isOpenFromOutside={isSupportChatOpen}
        onOpenFromOutside={handleOpenSupportChat}
        onReceiveNewMessages={handleNewMessages}
        onCheckAvailability={onSupportChatVisible}
      />
    </>
  );
};

export default AboutUsPage;
